import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import ImageUploader from "@/components/ImageUploader";
import GalleryUploader from "@/components/GalleryUploader";
import { Plus, Trash2, X, Edit3 } from "lucide-react";

const CATEGORIES = ["new_construction", "remodel", "renovation"];
const STATUSES = ["planning", "in_progress", "complete"];

const blank = {
  title: "",
  description: "",
  category: "new_construction",
  status: "complete",
  location: "",
  cover_image: "",
  before_image: "",
  after_image: "",
  gallery: [],
  featured: false,
  sqft: 0,
  completed_year: new Date().getFullYear(),
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, sqft: Number(editing.sqft) || null, completed_year: Number(editing.completed_year) || null };
    if (editing.id) {
      await api.put(`/projects/${editing.id}`, payload);
    } else {
      await api.post("/projects", payload);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <DashboardLayout
      role="admin"
      title="Projects"
      subtitle="Public portfolio that shows up on your marketing site."
      actions={<button className="btn-primary" onClick={() => setEditing({ ...blank })} data-testid="new-project-btn"><Plus size={16}/> Add Project</button>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="card overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="badge text-[10px]">{p.category.replace('_',' ')}</span>
                <span className={`badge text-[10px] ${p.status === 'complete' ? 'badge-green' : 'badge-muted'}`}>{p.status.replace('_',' ')}</span>
              </div>
              <h3 className="font-display text-lg font-bold mb-1">{p.title}</h3>
              <div className="text-xs mb-4" style={{color:'var(--text-muted)'}}>{p.location}</div>
              <div className="flex gap-2">
                <button className="btn-ghost !py-1.5 !px-3 !text-xs flex-1 justify-center" onClick={() => setEditing(p)} data-testid={`edit-project-${p.id}`}><Edit3 size={12}/> Edit</button>
                <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} style={{color:'#fca5a5'}}><Trash2 size={12}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Project" : "New Project"}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title"><input className="input" value={editing.title} onChange={(e)=>setEditing({...editing, title: e.target.value})} data-testid="proj-title"/></Field>
            <Field label="Location"><input className="input" value={editing.location} onChange={(e)=>setEditing({...editing, location: e.target.value})}/></Field>
            <Field label="Category"><select className="input" value={editing.category} onChange={(e)=>setEditing({...editing, category: e.target.value})}>{CATEGORIES.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}</select></Field>
            <Field label="Status"><select className="input" value={editing.status} onChange={(e)=>setEditing({...editing, status: e.target.value})}>{STATUSES.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}</select></Field>
            <Field label="Square feet"><input type="number" className="input" value={editing.sqft || ''} onChange={(e)=>setEditing({...editing, sqft: e.target.value})}/></Field>
            <Field label="Completed year"><input type="number" className="input" value={editing.completed_year || ''} onChange={(e)=>setEditing({...editing, completed_year: e.target.value})}/></Field>
            <Field label="Cover image" full>
              <ImageUploader
                value={editing.cover_image}
                onChange={(url) => setEditing({...editing, cover_image: url})}
                folder="coastal-bend/projects"
                testid="proj-cover"
              />
            </Field>
            <Field label="Before image">
              <ImageUploader
                value={editing.before_image}
                onChange={(url) => setEditing({...editing, before_image: url})}
                folder="coastal-bend/projects"
                testid="proj-before"
              />
            </Field>
            <Field label="After image">
              <ImageUploader
                value={editing.after_image}
                onChange={(url) => setEditing({...editing, after_image: url})}
                folder="coastal-bend/projects"
                testid="proj-after"
              />
            </Field>
            <Field label="Description" full><textarea className="input" rows={4} value={editing.description} onChange={(e)=>setEditing({...editing, description: e.target.value})}/></Field>
            <Field label="Gallery (drag & drop multiple photos)" full>
              <GalleryUploader
                value={editing.gallery || []}
                onChange={(urls) => setEditing({...editing, gallery: urls})}
                folder="coastal-bend/projects"
                testid="proj-gallery"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" checked={!!editing.featured} onChange={(e)=>setEditing({...editing, featured: e.target.checked})}/>
              Feature on homepage
            </label>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="proj-save">Save Project</button>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? 'md:col-span-2' : ''}`}>
      <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>{label}</div>
      {children}
    </label>
  );
}

// Tailwind JIT safelist — ensure dynamic max-w-{size} classes are generated
// eslint-disable-next-line no-unused-vars
const _MODAL_SIZE_SAFELIST = "max-w-sm max-w-md max-w-lg max-w-xl max-w-2xl max-w-3xl max-w-4xl max-w-5xl max-w-6xl max-w-7xl";

const SIZE_MAP = {
  "sm": "max-w-sm", "md": "max-w-md", "lg": "max-w-lg", "xl": "max-w-xl",
  "2xl": "max-w-2xl", "3xl": "max-w-3xl", "4xl": "max-w-4xl",
  "5xl": "max-w-5xl", "6xl": "max-w-6xl", "7xl": "max-w-7xl",
};

export function Modal({ children, onClose, title, size = "3xl" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.6)', backdropFilter:'blur(8px)'}} onClick={onClose}>
      <div className={`card p-6 w-full ${SIZE_MAP[size] || SIZE_MAP["3xl"]} max-h-[90vh] overflow-y-auto`} style={{background:'var(--surface)'}} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-[--text-muted] hover:text-white"><X size={20}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
